import app from "@/main";
import {IslemDurum, ITask, PlayerDurum, TaskManagerState, S, R, ITasks} from "./GorevYoneticisiTypes";
import {TaskDurum} from "../../enum/TaskDurum";
import {ActionContext} from "vuex";
import {TaskTarget} from "../../enum/TaskTarget";
import {TaskTuru} from "../../enum/TaskTuru";
import {sorguExcelDosyaAdi, sorguExceliIndirilmeliMi} from "./sorguExceli";
import {tikZamanlayiciKur} from "./tikZamanlayici";
import {AxiosResponse} from "axios";
import {TaskHandler} from "./TaskHandler/TaskHandler";
import {TaskResponser} from "./TaskResponser/TaskResponser";
import {bildirimMetni} from "../../bildirimMetni";

const taskHandler = new TaskHandler();
const taskResponser = new TaskResponser();

// taskInterval() içinde kurulan anında tetikleme fonksiyonuna referans.
// Player 'oto'ya geçtiğinde (Başlat) ilk görevin 5sn'lik tiki beklememesi için kullanılır.
let anindaTetikleRef: (() => void) | null = null;

/**
 * Planlı (scheduled_at ileri tarihli) paket için tam saatinde uyandırma.
 *
 * Geri çekilme 30 sn'ye kadar bekletebilir; kullanıcı "15:54'e planladım,
 * 15:54'te başlasın" bekliyor. Lokal liste her tazelendiğinde en yakın ileri
 * tarihli scheduled_at bulunur ve o ana bir zamanlayıcı kurulur; süresi gelince
 * anındaTetikle() next-task'ı hemen sorar. Pencere kısılmışsa zamanlayıcı en
 * geç ilk uyanışta (≤1 dk) çalışır — eskisi gibi dakikalar değil. Tek
 * zamanlayıcı tutulur; her tazelemede yenisi kurulur.
 */
let zamanlanmisUyandirmaHandle: ReturnType<typeof setTimeout> | null = null;

function zamanlanmisUyandirmayiKur(gorevler: Array<{scheduled_at?: unknown}>): void {
    if (zamanlanmisUyandirmaHandle !== null) {
        clearTimeout(zamanlanmisUyandirmaHandle);
        zamanlanmisUyandirmaHandle = null;
    }
    const simdi = Date.now();
    let enYakin: number | null = null;
    for (const g of gorevler) {
        const s = g && g.scheduled_at;
        if (!s) continue;
        const ms = new Date(s as any).getTime();
        if (isNaN(ms) || ms <= simdi) continue;
        if (enYakin === null || ms < enYakin) enYakin = ms;
    }
    if (enYakin === null) return;
    // Sunucu saatiyle küçük sapma payı: saatinden 500 ms sonra sor.
    zamanlanmisUyandirmaHandle = setTimeout(() => {
        zamanlanmisUyandirmaHandle = null;
        if (anindaTetikleRef !== null) anindaTetikleRef();
    }, Math.min(enYakin - simdi + 500, 2147483647));
}

/**
 * Lokal listenin sunucuyla eşitlenme temposu (tik sayısı; tik = 5 sn).
 * 12 tik ≈ 1 dakika: gece boyunca "bekleme" hâlindeki bot için ucuz, kullanıcı
 * İşlemler ekranına geçtiğinde liste bayat kalmayacak kadar sık.
 */
const AYNA_TAZELEME_TIK_ARALIGI = 12;

/**
 * Boş kuyrukta ardışık next-task cevaplarına göre kademeli geri çekilme.
 *
 * İndeks = üst üste kaç kez next-task 'boş' döndü (tavan: son eleman). Değer =
 * bir önceki sorudan itibaren geçmesi gereken EN AZ süre (ms). 5 sn'lik tiklerle
 * ardışık sorular arası 5, 10, 20, 30 sn'lik boşluk üretir.
 *
 * GERÇEK SAATE göre ölçülür, tik sayısına göre DEĞİL (9 Eyl 2026 HAR): pencere
 * arka plandayken Chromium tikleri dakikada bire kısıyordu; "5 tik bekle" 30 sn
 * yerine 5 dakika sürdü ve 15:54'e planlı paket 15:57'de başladı. Saat
 * ölçümüyle kısılmış tikte bile bekleme süresi dolduysa hemen sorulur.
 */
const GERI_CEKILME_ARALIGI_MS = [0, 10000, 20000, 30000];

/**
 * Ardışık HATADA (next-task/işleme patladı) bekleme: 5, 10, 20, 40 ... sn, tavan 300 sn.
 * Boş kuyruk geri çekilmesinden (yukarıdaki tablo) ayrıdır: o "iş yok" der, bu "sunucu/ağ
 * bozuk" der. İlk başarılı next-task cevabında sayaç sıfırlanır.
 */
export const HATA_GERI_CEKILME_ILK_MS = 5000;
export const HATA_GERI_CEKILME_TAVAN_MS = 300000;

export function hataGeriCekilmeMs(ardisikHataSayisi: number): number {
    if (ardisikHataSayisi <= 0)
        return 0;
    return Math.min(HATA_GERI_CEKILME_ILK_MS * Math.pow(2, ardisikHataSayisi - 1), HATA_GERI_CEKILME_TAVAN_MS);
}

/** Worker tiki saniyeler içinde oynayabilir; ilk adım (5 sn) bir tiki ıskalamasın. */
const HATA_TIK_TOLERANSI_MS = 500;

/**
 * Otomatik görev işleme (gece senkronu) tercihinin localStorage anahtarı.
 * Kullanıcıya görünen adı: "Otomatik görev işleme (gece senkronu)".
 */
export const OTOMATIK_GOREV_ISLEME_ANAHTARI = "otomatikGorevIsleme";

/**
 * Bırakılan eski anahtar. YALNIZ SİLMEK için duruyor — değeri hiçbir koşulda
 * okunmuyor (bkz. `initAutoPlay`).
 */
export const ESKI_OTOMATIK_OYNATMA_ANAHTARI = "autoPlay";

const state: TaskManagerState = {
    tasks: {
        completed_tasks: [],
        incomplete_tasks: [],
        processed_task_childs: []
    },
    islemDurum: IslemDurum.BOSTA,
    playerDurum: PlayerDurum.dur,
    autoPlay: false,
    enSonIslenenGorev: null,
    taskIntervalHandle: null
}

const getters = {
    // e.g. this.$store.getters.XXX
    getGorevler: (state: TaskManagerState) => state.tasks,
    getAutoPlay: (state: TaskManagerState) => state.autoPlay,
    getTamamlananGorevler: (state: TaskManagerState) => {
        return state.tasks.completed_tasks;
    },
    getSiradakiGorevler: (state: TaskManagerState) => {
        return state.tasks.incomplete_tasks;
    },
    getChildTasksStatus: (state: TaskManagerState) => {
        return state.tasks.processed_task_childs;
    },
    getIslemDurum: (state: TaskManagerState) => state.islemDurum,
    getPlayerDurum: (state: TaskManagerState) => state.playerDurum
}

const mutations = {
    // senkron işlemlerde kullanılır anlık olarak state'i günceller
    // e.g. this.$store.commit('fonksiyon adı', deger)
    updateGorevler(state: TaskManagerState, payload: ITasks) {
        state.tasks = payload;
    },
    playerAuto(context: ActionContext<S, R>) {
        context.commit("setPlayerDurum", PlayerDurum.oto);
    },
    setPlayerDurum(state: TaskManagerState, payload: PlayerDurum) {
        const oncekiDurum = state.playerDurum;
        state.playerDurum = payload;
        // 'Başlat'a basıldığında ilk görev bir sonraki 5sn'lik tiki beklemesin.
        // (PlayerDurum.oto = 0 olduğu için açık karşılaştırma şart.)
        if (payload == PlayerDurum.oto && oncekiDurum != PlayerDurum.oto && anindaTetikleRef !== null)
            anindaTetikleRef();
    },
    /**
     * Otomatik görev işleme tercihini yükler ve gerekiyorsa TOHUM GÖÇÜNÜ yapar.
     *
     * ESKİ ANAHTAR OKUNMAZ. Bugüne kadarki `autoPlay` kayıtlarının neredeyse
     * tamamı `false`tı ve bu bilinçli bir kullanıcı tercihi DEĞİLDİ: eski
     * `initAutoPlay` kayıt yokken sessizce `false` tohumluyordu. Bu kayıtları
     * taşımak, kullanıcı hiç dokunmamışken "kapatmış" saymak olurdu — okunmuyor
     * ve görülürse siliniyor.
     *
     * TOHUM VARSAYILANI UYGULAMANIN KENDİSİNDEN GELİR (`varsayilan`), çünkü
     * bayrak iki uygulamada FARKLI anlam taşıyor: bot uygulamasında (vue-imza)
     * "açılışta görev işlemeye başla" demek ve gece senkronu için varsayılanı
     * TRUE olmalı; icrapro-app'te ise İşlemler ekranındaki "işlem oluştuktan
     * sonra başlat" anahtarı — TRUE tohumlansaydı, anahtara hiç dokunmamış
     * kullanıcının oluşturduğu toplu haciz/harç paketi elle Başlat olmadan
     * ANINDA koşar, yani ÖDEME tetiklerdi. Parametresiz çağrı bu yüzden güvenli
     * tarafa (false) düşer. Origin'ler ayrı localStorage kullandığı için iki
     * uygulamanın kayıtları birbirine karışmaz.
     */
    initAutoPlay(state: TaskManagerState, varsayilan?: boolean) {
        const tohum = varsayilan === true;
        try {
            // Eski kayıt bilinçsiz tohumdu; taşınmaz, temizlenir.
            localStorage.removeItem(ESKI_OTOMATIK_OYNATMA_ANAHTARI);

            const kayit = localStorage.getItem(OTOMATIK_GOREV_ISLEME_ANAHTARI);
            if (kayit === null) {
                state.autoPlay = tohum;
                localStorage.setItem(OTOMATIK_GOREV_ISLEME_ANAHTARI, JSON.stringify(tohum));
                return;
            }
            // Bozuk/elle düzenlenmiş değerde varsayılana düşülür.
            state.autoPlay = JSON.parse(kayit) === true;
        } catch (e) {
            state.autoPlay = tohum;
        }
    },
    updateAutoPlay(state: TaskManagerState, payload: boolean) {
        state.autoPlay = payload === true;
        try {
            localStorage.setItem(OTOMATIK_GOREV_ISLEME_ANAHTARI, JSON.stringify(state.autoPlay));
        } catch (e) {
            console.warn("Otomatik görev işleme tercihi kaydedilemedi:", e);
        }
    }
}

const actions = {
    // asenkron state güncelleme işlemlerinde kullanılır
    // e.g. this.$store.dispatch('fonksiyon adı', deger)
    async getTasksFromServer(context: ActionContext<S, R>) {
        return new Promise((resolve, reject) => {
            app.$http?.get('/api/v1/task').then((response: AxiosResponse<ITasks>) => {
                context.state.tasks = response.data;
                app.$socket.client.emit("updateTasks");
                resolve(response);
            }).catch((error: Error) => {
                reject(error);
            })
        })
    },
    async updateTasks(context: ActionContext<S, R>) {
        // Tazeleme ÖNCESİ bilinen sıradaki görevler. Sunucudan yeni bir görev
        // gelmişse (tarayıcıdan açılan Takip Sorgula vb.) bot 5 sn'lik tiki ya da
        // geri çekilmiş next-task temposunu (30 sn'e kadar) beklememeli: bu action
        // socket 'updateTasks' olayıyla da tetiklenir, yani karşı uygulamanın
        // "görev açtım" sinyali tam buradan geçer. 8 Eyl ölçümü: görev 12:46:44'te
        // açıldı, bot 12:48:16'da aldı — geri çekilme + 60 sn'lik ayna üst üste.
        const oncekiIdler = new Set<string>(
            (context.state.tasks?.incomplete_tasks ?? []).map((g: ITask) => String(g.id))
        );
        return new Promise((resolve, reject) => {
            app.$http?.get('/api/v1/task').then((response: AxiosResponse<ITasks>) => {
                context.state.tasks = response.data;
                const yeniGorevVar = (response.data?.incomplete_tasks ?? [])
                    .some((g: ITask) => !oncekiIdler.has(String(g.id)));
                if (yeniGorevVar && context.getters.getPlayerDurum == PlayerDurum.oto && anindaTetikleRef !== null)
                    anindaTetikleRef();
                zamanlanmisUyandirmayiKur(response.data?.incomplete_tasks ?? []);
                resolve(response);
            }).catch((error: Error) => {
                reject(error);
            })
        })
    },
    setTasks(context: ActionContext<S, R>,tasks: ITasks) {
        context.state.tasks = tasks;
    },
    async playerStop(context: ActionContext<S, R>) {
        context.commit("setPlayerDurum", PlayerDurum.durduruluyor);
        // Çapraz paket seçimiyle birden fazla paket başlamış olabilir: biri ISLENIYOR,
        // diğerleri BEKLEMEDE. find() yalnızca ilkini bulup gerisini asılı bırakıyordu.
        let baslamisAnaGorevler: Array<ITask> = context.state.tasks.incomplete_tasks.filter(
            (task: ITask) => task.status == TaskDurum.ISLENIYOR || task.status == TaskDurum.BEKLEMEDE
        );
        for (const anaGorev of baslamisAnaGorevler) {
            await context.dispatch('putTask', {id: anaGorev.id, status: TaskDurum.DURAKLATILDI});
        }
    },
    taskInterval(context: ActionContext<S, R>) {
        // Zaten interval çalışıyorsa tekrar başlatma (double-start prevention)
        if (context.state.taskIntervalHandle !== null) {
            return;
        }

        // kod inceleme (T2-36): dönen handle'ın durdur() ucu burada/uygulama genelinde
        // hiç çağrılmıyor — bilinçli: bot süreci kuyruğun sahibi olan sunucudan
        // koşulsuz next-task çektiği için bu döngü tasarım gereği sürecin tüm ömrü
        // boyunca çalışır. durdur() yalnız testler (tikZamanlayici.spec) içindir;
        // gerçek bir sökme noktası (oturum kapanışı/logout) eklenirse handle.durdur()
        // orada çağrılmalıdır.

        // Görev bittikten sonra bir sonraki 5sn'lik tiki beklemek, görev süresi kısaldıkça
        // oransal olarak büyüyen bir ölü bekleme yaratıyordu (kısa görevlerde ~%17).
        // anindaTetikle() döngüyü bir sonraki tiki beklemeden yeniden çalıştırır.
        // Bayrak, aynı anda birden fazla tetiklemenin kuyruğa girmesini engeller.
        let anindaTetikBekliyor = false;
        // Boş kuyruk geri çekilmesi: bkz. GERI_CEKILME_ARALIGI_MS.
        // ardisikBosCevapSayisi = üst üste kaç kez next-task 'boş' döndü.
        // sonSoruZamaniMs = next-task'ın en son sorulduğu an (Date.now()).
        let ardisikBosCevapSayisi = 0;
        let sonSoruZamaniMs = 0;
        // Ardışık hata sayacı ve "bu hata bölümü için toast gösterildi mi" bayrağı.
        // Bölüm = ilk hatadan ilk başarılı cevaba kadar; toast bölüm başında BİR kez atılır.
        let ardisikHataSayisi = 0;
        let hataBildirildi = false;
        // Son yayınlanan 'Waiting' metni: aynı metin her 5 sn yeniden yayınlanmaz.
        let sonWaitingMesaji: string | null = null;
        // Bağlantıyı sürdürme ve kimlik güvenli noktası için uçuş bayrakları: tik
        // yeni başlamadan öncekiler bitmediyse ikinci bir çağrı açılmaz.
        let baglantiSurduruluyor = false;
        let guvenliNoktaCalisiyor = false;
        const anindaTetikle = () => {
            if (anindaTetikBekliyor)
                return;
            anindaTetikBekliyor = true;
            // Anında tetikleme "hemen sor" demektir; geri çekilme sayacı da
            // bununla tutarlı sıfırlanır (aksi halde hemen tetiklenen dongu()
            // bir önceki boş-cevap temposuna takılıp yine de beklerdi).
            ardisikBosCevapSayisi = 0;
            sonSoruZamaniMs = 0;
            // setTimeout(0) ile makrotask'a bırakılır; doğrudan çağrı yığın büyütür.
            setTimeout(() => {
                anindaTetikBekliyor = false;
                // dongu() reddedilirse yakalanmamış promise reddi oluşmasın (bkz. tik geri çağrısı).
                dongu().catch((e: any) => console.warn('Bot döngüsü hatası:', e));
            }, 0);
        };
        // Başlat'a basıldığında mutation tarafından çağrılabilmesi için dışarı ver.
        anindaTetikleRef = anindaTetikle;

        // eİcraPro kimliği (API token'ı) imzacıya devredilmeden döngü HİÇBİR ŞEY
        // yapmaz: next-task/updateTasks sormaz, UYAP'a yeniden bağlanmayı da
        // denemez. Eskiden kimlik gelmeden de soruluyordu; Electron proxy'sinin
        // API hedefi henüz boş olduğu için her istek "Host should not be empty"
        // ile düşüyordu (DEV-2603, 9 Eki yorumu). Player 'oto'da kalır, kimlik
        // gelince bir sonraki tikte kendiliğinden devam edilir.
        // Getter yalnız bot uygulamasında (vue-imza-app) tanımlı; tanımsızsa
        // (icrapro-app) kapı açık sayılır, davranış değişmez.
        const eicraproBekleniyor = () => context.rootGetters?.isEicraproBagli === false;

        // Kök action'ı olmayan eski host için yedek (checkUyapSession yalnız o host'ta var):
        // bir kez yeniden bağlanmayı dener, olmazsa eskisi gibi botu durdurur. Durdurma
        // login-spam sınırıdır; yeni host'taki geri çekilme/tavan kapıları burada yok.
        // eİcraPro kimlik kapısı dongu() başında geçildi, yani kimlik bekleyen host'ta
        // buraya inilmez. Eski sıfırlama (ISLENIYOR → BOSTA) aynı görevin iki kez
        // alınmasına yol açtığı için YOK; yalnız durdurma yolunda sıfırlanır.
        const eskiYenidenBaglanma = async () => {
            try {
                await context.dispatch('checkUyapSession', null, { root: true });
                if (!(context.rootGetters?.isUyapConnected ?? false))
                    throw new Error('Reconnect başarısız: UYAP bağlantısı hala yok');
                app.$toast.info('UYAP bağlantısı yeniden sağlandı.');
            } catch (error) {
                app.$toast.error('UYAP bağlantısı kesildi. Görevler durduruldu. Lütfen imzacıyı kontrol edin.');
                context.commit("setPlayerDurum", PlayerDurum.dur);
                context.state.enSonIslenenGorev = null;
                context.state.islemDurum = IslemDurum.BOSTA;
            }
        };

        const dongu = async () => {
            if (eicraproBekleniyor()) {
                // Kimlik yokken/geçersizken API'ye PUT atılamaz (istek zaten düşer); ama
                // kullanıcının Durdur'u yine de tamamlanmalı: aksi halde player
                // 'durduruluyor'da kalır ve Başlat, kimlik gelene kadar kapalı kalır.
                // Askıdaki üst görev kimlik dönünce next-task ile kaldığı yerden sürer.
                if (context.getters.getPlayerDurum == PlayerDurum.durduruluyor) {
                    app.$socket.client.emit("player", PlayerDurum.dur);
                    context.state.enSonIslenenGorev = null;
                }
                return;
            }
            const isUyapConnected = context.rootGetters?.isUyapConnected ?? false;

            // Bağlantı düşmüşse ve player "oto" ise bağlantıyı SÜRDÜR. Döngü bağlanmayı
            // kendisi yapmaz ve bağlanamamayı bir hata saymaz: player 'oto'da kalır
            // (yalnız güvenlik durumları ve kullanıcının Durdur'u botu durdurur),
            // islemDurum/enSonIslenenGorev'e DOKUNULMAZ (askıdaki görev kurtarma
            // bitince kaldığı yerden sürer; sıfırlamak aynı görevin ikinci kez
            // alınmasına yol açıyordu), toast yoktur. Kapıları (PIN, kart, geri
            // çekilme, saatlik tavan) uygulamanın kök action'ı uygular. Önceki
            // çağrı sürerken yenisi açılmaz.
            if (!isUyapConnected && context.getters.getPlayerDurum == PlayerDurum.oto) {
                if (baglantiSurduruluyor)
                    return;
                baglantiSurduruluyor = true;
                try {
                    const surdurme = context.dispatch('botBaglantisiniSurdur', null, { root: true });
                    // Vuex bilinmeyen action'da Promise değil undefined döner. Yayın penceresi
                    // yedeği: imzacı pipeline'ı plugin'i dal ucuyla derler; plugin host'tan önce
                    // yayına girerse eski host'ta bu kök action yoktur ve boştaki bot bir daha
                    // bağlanmazdı. O durumda ESKİ davranışa düşülür (host güncellenince kalkar).
                    if (surdurme === undefined)
                        await eskiYenidenBaglanma();
                    else
                        await surdurme;
                } catch (error) {
                    console.warn('Bot UYAP bağlantısını sürdüremedi:', error);
                } finally {
                    baglantiSurduruluyor = false;
                }
                return; // Bu döngüde işlem yapma
            }

            // KUYRUĞUN SAHİBİ SUNUCUDUR (Faz 3 sonrası). Eskiden burada
            // `getSiradakiGorevler.length > 0` koşulu vardı ve `next-task` yalnız
            // LOKAL liste doluyken sorulurdu. Senkron planları görevleri SUNUCUDA
            // üretmeye başlayınca bu varsayım kırıldı: gece üretilen paket lokal
            // listeye hiç düşmediği için bot, "oto"da olsa bile next-task'ı HİÇ
            // sormuyordu. Artık oto modda LOKAL LİSTEDEN BAĞIMSIZ sorulur; sunucuda
            // iş yoksa cevap ucuz bir 'Görev bulunamadı'dır. Ardışık boş cevaplarda
            // ise tempo GERI_CEKILME_ARALIGI_MS ile kademeli geri çekilir (aşağıda).
            if (isUyapConnected &&
                context.getters.getPlayerDurum == PlayerDurum.oto &&
                context.getters.getIslemDurum == IslemDurum.BOSTA) {
                // Kimlik güvenli noktasındaki await sürerken gelen tikler girmez.
                if (guvenliNoktaCalisiyor)
                    return;
                // Geri çekilme temposu: ardışık boş cevaplardan sonra next-task,
                // son sorudan itibaren GERI_CEKILME_ARALIGI_MS kadar süre geçmeden
                // sorulmaz (gerçek saat; kısılmış tikte de doğru). islemDurum
                // BOSTA'da kalır; sonraki tik yeniden bakar.
                const enAzAralik = GERI_CEKILME_ARALIGI_MS[Math.min(ardisikBosCevapSayisi, GERI_CEKILME_ARALIGI_MS.length - 1)];
                if (ardisikBosCevapSayisi > 0 && Date.now() - sonSoruZamaniMs < enAzAralik) {
                    return;
                }
                // Hata geri çekilmesi: ardışık hatada sunucuya 5 sn'de bir vurulmaz.
                if (ardisikHataSayisi > 0 &&
                    Date.now() - sonSoruZamaniMs < hataGeriCekilmeMs(ardisikHataSayisi) - HATA_TIK_TOLERANSI_MS) {
                    return;
                }
                // GÜVENLİ NOKTA: bot boşta, görev almak üzere. Bekleyen bir kimlik
                // geçişi (aynı büro token yenilemesi, onaylanmış hesap geçişi) ancak
                // burada uygulanır; görev işlenirken kimlik asla değişmez. Kök action
                // yalnız imzacıda tanımlı (icrapro-app döngüyü çalıştırmaz). islemDurum
                // BOSTA kalır ki action "bot boşta" görsün.
                // sonSoruZamaniMs await'ten ÖNCE yazılır: anindaTetikle() bu değeri
                // 0'lıyor; await'ten sonra yazılsaydı o sıfırlamayı ezerdi.
                sonSoruZamaniMs = Date.now();
                guvenliNoktaCalisiyor = true;
                try {
                    await context.dispatch('bekleyenKimligiUygula', null, { root: true });
                } catch (error) {
                    console.warn('Bekleyen kimlik uygulanamadı:', error);
                } finally {
                    guvenliNoktaCalisiyor = false;
                }
                // Action sırasında durum değişmiş olabilir (geçiş player'ı durdurur,
                // UYAP bağlantısı düşer, kimlik bekleme durumuna girer): yeniden doğrula.
                if (eicraproBekleniyor() ||
                    !(context.rootGetters?.isUyapConnected ?? false) ||
                    context.getters.getPlayerDurum != PlayerDurum.oto ||
                    context.getters.getIslemDurum != IslemDurum.BOSTA) {
                    return;
                }
                context.state.islemDurum = IslemDurum.ISLENIYOR;
                // Anında tetikleme SADECE gerçekten görev işlendiğinde yapılır.
                // 'Waiting' (evrak gönder birim beklemesi) ve hata durumlarında normal
                // 5sn'lik tempoya düşülür; 'Görev bulunamadı' durumunda ise yukarıdaki
                // geri çekilme tablosuna göre 5→10→20→30sn'e kadar yavaşlanır. Her
                // durumda anında tetiklenmediği için next-task ucuna sıkı döngü
                // (tight loop) oluşmaz.
                let gorevIslendi = false;
                try {
                    let nextTaskResponse: any = await app.$http.get('/api/v1/next-task');
                    const nextTask = nextTaskResponse.task;
                    if (nextTaskResponse.status == 'Success') {
                        // Görev başka bir büroya aitse işlenmez: aktif kimlik (imzacıda
                        // getter tanımlı) ile görevin bürosu aynı olmalı. Getter TANIMSIZ
                        // (undefined: icrapro-app) ya da görevde tenant_id yoksa denetim yok;
                        // getter null (aktif kimlik yok) ise görev işlenmez (fail-closed).
                        const aktifTenantId = context.rootGetters?.aktifTenantId;
                        if (aktifTenantId !== undefined) {
                            const aktif = aktifTenantId === null ? '' : String(aktifTenantId);
                            const gorevTenant = nextTask?.tenant_id == null ? '' : String(nextTask.tenant_id);
                            if (aktif === '') {
                                console.error('Aktif büro kimliği yok, next-task görevi işlenmiyor.');
                                throw new Error('Aktif büro kimliği belli olmadığı için görev işlenmedi.');
                            }
                            if (gorevTenant !== '' && gorevTenant !== aktif) {
                                console.error('next-task başka bir büronun görevini verdi, işlenmiyor.',
                                    {gorevTenant: gorevTenant, aktifTenant: aktif});
                                throw new Error('Sunucudan gelen görev bu büroya ait değil, işlenmedi.');
                            }
                        }
                        context.state.enSonIslenenGorev = nextTask;
                        // Görev geldi: kuyruk boş değilmiş, geri çekilme sıfırlanır.
                        ardisikBosCevapSayisi = 0;
                        sonWaitingMesaji = null;
                        if (nextTask.parent_id)
                            await taskHandler.handle(nextTask);
                        else {
                            await taskResponser.response(nextTask);
                            // Senkron paketinde (scheduled_at dolu) Excel İNMEZ; elle
                            // açılan sorguda zaman damgalı adla iner (bkz. sorguExceli.ts).
                            if (nextTask.task_type_id === TaskTuru.takip_sorgula && sorguExceliIndirilmeliMi(nextTask))
                                app.$helper.download(sorguExcelDosyaAdi(), '/api/v1/export-to-excel/' + nextTask.id);
                        }
                        // Bayrak işlem bittikten SONRA kalkar. Önce kalkarsa görev hata
                        // atınca da anında tetikleme yapılır ve yukarıda anlatılan sıkı
                        // döngü hata durumunda oluşur.
                        gorevIslendi = true;
                    } else if (nextTaskResponse.status == 'Waiting') {
                        // Kuyruk boş DEĞİL, yalnız evrak gönderilecek birim bekleniyor;
                        // bu "boş kuyruk" değildir, geri çekilme uygulanmaz/sıfırlanır.
                        ardisikBosCevapSayisi = 0;
                        // Bildirim yalnız METİN DEĞİŞİNCE yayınlanır; her 5 sn'de aynı
                        // mesajı yeniden basmak imzacıda toast yığıyordu. Metin boşsa
                        // (sunucu mesaj vermediyse) boş bildirim yayınlanmaz.
                        const mesaj = typeof nextTaskResponse.message === 'string' ? nextTaskResponse.message.trim() : '';
                        // Sunucu mesajı kalan süreyi içerir ('... kalan süre : 4 dk 52 sn') ve her
                        // yoklamada değişir; karşılaştırma anahtarı geri sayım parçalarından
                        // arındırılır, yoksa her 5 sn'de yeni toast çıkardı. Gösterilen metin tam kalır.
                        const anahtar = mesaj.replace(/\d+\s*(?:dk|sn)/g, '').replace(/\s+/g, ' ').trim();
                        if (mesaj !== '' && anahtar !== sonWaitingMesaji) {
                            sonWaitingMesaji = anahtar;
                            let eventData = {
                                message: mesaj,
                                seconds: 5
                            }
                            app.$socket.client.emit("notification",eventData);
                        }
                    } else {
                        // 'Görev bulunamadı': ardışık boş cevap sayısı bir artar ve
                        // bir sonraki sorunun kaç tik erteleneceği tablodan okunur.
                        sonWaitingMesaji = null;
                        ardisikBosCevapSayisi = Math.min(ardisikBosCevapSayisi + 1, GERI_CEKILME_ARALIGI_MS.length - 1);
                    }
                    // Tur hatasız bitti (görev işlendi / bekleniyor / kuyruk boş): hata
                    // bölümü kapanır. Sıfırlama BURADA, yani try'ın sonunda: next-task'a
                    // ulaşıp sonra patlayan tur (ör. başka büronun görevi) başarı sayılmaz.
                    ardisikHataSayisi = 0;
                    hataBildirildi = false;
                } catch (e) {
                    // Hata bölümü başında BİR toast (metin: bildirimMetni, nesne değil);
                    // sürdükçe toast yok, bekleme 5→300 sn büyür (hataGeriCekilmeMs).
                    ardisikHataSayisi += 1;
                    console.error('Bot döngüsü hatası (' + ardisikHataSayisi + '. ardışık):', e);
                    // 423 (imza/lisans kimlik uyuşmazlığı): diyalog ve duraklatma interceptor'dan
                    // gelir, ayrıca toast atılmaz; sayaç ve geri çekilme yine işler.
                    if (!hataBildirildi && e?.response?.status !== 423) {
                        hataBildirildi = true;
                        app.$toast.error(bildirimMetni(e));
                    }
                } finally {
                    context.state.islemDurum = IslemDurum.BOSTA;
                    console.log('Bitti Boşa Çıktı', context.state.islemDurum);
                    // Görev işlendiyse ve player hâlâ otomatikse sıradakine hemen geç.
                    // Aksi halde 5sn'lik interval güvenlik ağı olarak devreye girer.
                    // GÖREV BAŞINA AYRICA `updateTasks` ÇAĞRILMAZ: liste zaten
                    // tazeleniyor. TaskHandler görev başına iki kez `putTask`
                    // ediyor (SIRADA->ISLENIYOR ve bitiş), `putTask` da her PUT
                    // sonrası `getTasksFromServer` çağırıyor. Buraya bir dispatch
                    // daha koymak 200 görevlik bir pakette 200 gereksiz
                    // GET /api/v1/task demekti (%50 fazla yük).
                    if (gorevIslendi && context.getters.getPlayerDurum == PlayerDurum.oto)
                        anindaTetikle();
                }
            } else if (context.getters.getPlayerDurum == PlayerDurum.durduruluyor) {
                // ITask'ta tenant_id alanı tanımlı değil (sunucu cevabında var): any.
                const son: any = context.state.enSonIslenenGorev;
                try {
                    // Hatırlanan görev başka bürenin olabilir (kimlik/büro bu arada değişti, ör.
                    // 'Geç'): yeni kimlikle o görevin üstüne PUT atılırsa DEV-3897 yalıtımı 404
                    // verir. Getter tanımsızsa (icrapro-app) ya da görevde tenant_id yoksa denetim
                    // yok; getter null (aktif kimlik yok) ise PUT atılmaz. Süren paketi büro
                    // değişiminden önce eski kimlikle zaten oyuncuyuDuraklat duraklatır.
                    const aktifTenantId = context.rootGetters?.aktifTenantId;
                    const gorevTenant = son?.tenant_id == null ? '' : String(son.tenant_id);
                    const baskaBuro = aktifTenantId !== undefined && gorevTenant !== '' &&
                        gorevTenant !== (aktifTenantId === null ? '' : String(aktifTenantId));
                    if (son != null && son.parent_id != null && !baskaBuro)
                        await context.dispatch('putTask', {
                            id: son.parent_id,
                            status: TaskDurum.DURAKLATILDI
                        });
                } catch (e) {
                    console.warn('Üst görev duraklatılamadı:', e);
                } finally {
                    // PUT ne olursa olsun player 'dur'a iner ve bayat referans silinir; aksi
                    // halde player 'durduruluyor'da kilitlenir ve her tikte aynı PUT tekrarlanır.
                    app.$socket.client.emit("player", PlayerDurum.dur);
                    context.state.enSonIslenenGorev = null;
                }
            }
            // BOŞ KUYRUKTA PLAYER DURDURULMAZ. Eskiden burada
            // `length == 0 && oto -> emit player dur` vardı; gece senkronu
            // modelinde bu, üretim saatinden ÖNCE botu kendi kendine kapatıyordu.
            // "Oto" bir işleme durumu değil, GÖREV BEKLEME durumudur.
        };

        // "Uygulamayı aç, gerisine karışma": kullanıcı otomatik oynatmayı açık
        // bıraktıysa bot açılışta da oto'ya geçer. Bugüne dek `autoPlay` yalnız
        // istemci-üretimli akışta (postTasks -> autoPlayer) devreye giriyordu,
        // dolayısıyla gece senkronu için elle "Başlat" gerekiyordu.
        if (context.state.autoPlay && context.getters.getPlayerDurum == PlayerDurum.dur) {
            context.commit("setPlayerDurum", PlayerDurum.oto);
            app.$socket?.client?.emit("player", PlayerDurum.oto);
        }

        // Lokal listeyi sunucuyla eşitleme temposu: her tikte çekmek 5 sn'de bir
        // istek demek olurdu. Görev işlendikçe liste zaten `putTask` zinciriyle
        // tazeleniyor; buradaki seyrek tur, HİÇ görev işlenmeyen bekleme hâlinde
        // (gece senkronunun asıl senaryosu) listenin sunucudan haberdar olmasını
        // sağlar.
        let tikSayaci = 0;

        // Tik kaynağı Web Worker (Chromium arka plan kısıtlamasına girmez);
        // Worker kurulamazsa setInterval'e düşer. Ayrıntı: tikZamanlayici.ts.
        context.state.taskIntervalHandle = tikZamanlayiciKur(5000, () => {
            tikSayaci += 1;
            // GÖREV İŞLENİRKEN TAZELEME YAPILMAZ. `putTask` zaten iş sırasında
            // fire-and-forget `getTasksFromServer` çağırıyor; buradan üçüncü bir
            // eşzamanlı GET eklemek, cevaplar sırasız döndüğünde bayat listenin
            // taze listeyi ezmesine yol açıyordu (iki action da state.tasks'ı
            // sıra denetimi olmadan doğrudan yazıyor).
            if (tikSayaci % AYNA_TAZELEME_TIK_ARALIGI == 0 &&
                !eicraproBekleniyor() &&
                context.getters.getPlayerDurum == PlayerDurum.oto &&
                context.getters.getIslemDurum == IslemDurum.BOSTA) {
                // Yeni görev tespiti ve anında tetikleme updateTasks'ın içinde
                // (socket tetiklemesiyle ortak yol); burada yalnız çağrılır.
                context.dispatch('updateTasks').catch(() => undefined);
            }
            // 5 saniyelik interval güvenlik ağı: anında tetiklemenin yapılmadığı
            // (bekleme/hata/boş kuyruk) durumlarda döngüyü canlı tutar. Reddedilen söz
            // yakalanır (yakalanmamış promise reddi olmasın).
            dongu().catch((e: any) => console.warn('Bot döngüsü hatası:', e));
        });
    },
    async tebligatGideriGuncelle(context: ActionContext<S, R>, payload: {
        ids: Array<number>,
        tebligat_gideri: {
            tutar: string,
            aciklama: string,
            enabled: boolean
        }
    }) {
        return new Promise(async (resolve, reject)=>{
            try {
                await app.$http.post('/api/v1/task/tebligat-gideri-guncelle', payload);
                await context.dispatch('getTasksFromServer');
                // @ts-ignore
                resolve();
            } catch (e){
                reject(e);
            }
        })
    },
    async postTasks(context: ActionContext<S, R>, payload: Array<ITask>) {
        let _payload: any = {parent_id: null};
        if (payload.length > 0)
            _payload = payload[0];
        if (payload.length == 0 || _payload.parent_id == null) {
            // Parrent id yok, parrentı oluştur sonra görev paketini ekle
            let tasks = await context.dispatch('getTasksFromServer');
            let oncelikSirasi;
            if (tasks.data.length > 0) {
                let arr = tasks.data.filter((task: ITask) => (!(task.status == TaskDurum.BITTI_BASARILI || task.status == TaskDurum.BITTI_HATALI) && task.parent_id == null));
                if (arr.length > 0) {
                    arr.reverse();
                    oncelikSirasi = arr[0].oncelik_sirasi + 1;
                } else
                    oncelikSirasi = 1;
            } else {
                oncelikSirasi = 1;
            }
            // @ts-ignore
            let parentTask: ITask = {
                avukat_id: null,
                task_type_id: payload[0].task_type_id,
                oncelik_sirasi: oncelikSirasi,
                data: payload[0].data,
                task_target: (payload[0].task_target == TaskTarget.dosya ? TaskTarget.toplu : TaskTarget.dosya),
                status: TaskDurum.SIRADA
            };
            if (payload[0].aciklama) {
                parentTask.aciklama = payload[0].aciklama;
                delete payload[0].aciklama;
            } else {
                let aciklama = await context.dispatch("gorevAciklamasiOlustur", {
                    anaTask: parentTask,
                    childTasklar: payload
                });
                parentTask.aciklama = aciklama;
            }
            let parentTaskRes = await app.$http?.post('/api/v1/task', parentTask);
            let parentId = parentTaskRes.data.id;
            payload.forEach((task) => (task.parent_id = parentId));
            await app.$http?.post('/api/v1/toplutask', payload);
            await context.dispatch('autoPlayer', parentId);
            app.$toast?.info('Görevler sıraya alındı');
            return parentTaskRes;
        } else {
            // parrent_id var direkt post et
            app.$http?.post('/api/v1/toplutask', payload).then(() => {
                context.dispatch('getTasksFromServer');
                app.$toast?.info('Görevler sıraya alındı');
            })
        }
    },
    async autoPlayer(context: ActionContext<S, R>, payload: number) {
        console.log("autoPlayer - autoPlay:", context.state.autoPlay);
        if (context.state.autoPlay) {
            console.log("autoPlayer - playerDurum: DUR", context.state.playerDurum == PlayerDurum.dur);
            if (context.state.playerDurum == PlayerDurum.dur) {
                console.log("autoPlayer - siradakiGorevler.length:", context.getters.getSiradakiGorevler.length);
                if (context.getters.getSiradakiGorevler.length > 0) {
                    console.log("autoPlayer - oncelik-sirasi post ediliyor");
                    await app.$http?.post('/api/v1/task/oncelik-sirasi/' + payload);
                }
                console.log("autoPlayer - getTasksFromServer")
                await context.dispatch('getTasksFromServer');
                console.log("autoPlayer - player emit oto")
                app.$socket.client.emit("player", PlayerDurum.oto);
            }
        } else
            await context.dispatch('getTasksFromServer');
    },
    async putTask(context: ActionContext<S, R>, payload: ITask) {
        return await app.$http?.put('/api/v1/task/' + payload.id, payload).then(() => {
            context.dispatch('getTasksFromServer');
        });
    },
    deleteTask(context: ActionContext<S, R>, payload: ITask) {
        app.$http?.delete('/api/v1/task/' + payload.id).then(() => {
            context.dispatch('getTasksFromServer');
        })
    },
    async deleteAllTasks(context: ActionContext<S, R>, parentTask: ITask) {
        app.$http?.delete('/api/v1/task/' + parentTask.id).then(() => {
            context.dispatch('getTasksFromServer');
        })
    },
    waitThisLong(saniye: number) {
        return new Promise(resolve => setTimeout(resolve, saniye * 1000))
    },
    gorevAciklamasiOlustur(context: ActionContext<S, R>, payload: { anaTask: ITask, childTasklar: Array<ITask> }) {
        return new Promise((resolve) => {
            let aciklama = "";
            let takipler = payload.childTasklar.map(takip => (takip.birim_adi + " - " + takip.dosya_esas_no)).join(", ");
            let takiplerEsasNoYok = payload.childTasklar.map(takip => (takip.birim_adi)).join(", ");
            switch (payload.anaTask.task_type_id) {
                case TaskTuru.takip_sorgula:
                    aciklama = payload.anaTask.data.sorguAlanlari.length + " tane alan sorgulaması. (" + takipler + ")";
                    break;
                case TaskTuru.evrak_gonder:
                    payload.childTasklar.forEach(task => {
                        aciklama += task.dosya_esas_no + " - " + task.birim_adi + " dosyasına ";
                        task.data.forEach((evrak: any, index: number) => {
                            aciklama += evrak.dosyaData.name;
                            if (evrak.ekDosyalarData.length > 0) {
                                aciklama += ' ve ' + evrak.ekDosyalarData.map((ekEvrak: any) => ekEvrak.name).join(', ') + ' ek evraklı';
                            }
                            aciklama += " " + evrak.seciliTur.aciklama;
                            if (index + 1 !== task.data.length)
                                aciklama += ', ';
                        });
                        aciklama += " gönderilmesi. "
                    });
                    break;
                case TaskTuru.hazir_evrak_gonder:
                    aciklama = 'Evrak Türü: ' + payload.anaTask.data.evrakTur.aciklama + '. ' + payload.anaTask.data.dilekce.title +
                        ' gönderilmesi. Üçüncü şahıs bulunan dosyalara ' + (payload.anaTask.data.ucuncuSahsaGonder ? 'göderilsin.' : 'gönderilmesin.') +
                        ' (' + takipler + ')';
                    break;
                case TaskTuru.harc_masraf_ode:
                    aciklama += payload.anaTask.data.map((harcMasraf: any) => harcMasraf.adi).join(', ') + ' ödemelerinin yapılması. (' + takipler + ")";
                    break;
                case TaskTuru.mts_odeme:
                    aciklama += 'İcra takibi başlatma sebebi: ' + payload.anaTask.data.icraTakipSebep.isim + ' (' + takipler + ')';
                    break;
                case TaskTuru.toplu_genel_talep_hazirla:
                    aciklama = payload.anaTask.data.params.digerTalepParams.map((alan: any) => alan.talepKisaAdi).join(', ') + ' talebinin gönderilmesi. (' + takipler + ')';
                    break;
                case TaskTuru.toplu_haciz_gonder:
                    console.log('DATAAA:', payload.anaTask.data);
                    aciklama = payload.anaTask.data.params.hacizParams.map((alan: any) => alan.talepKisaAdi).join(', ') + ' gönderilmesi. (' + takipler + ')';
                    break;
                case TaskTuru.toplu_tebligat_hazirla:
                    aciklama = payload.anaTask.data.params.tebligatParams.map((alan: any) => alan.talepKisaAdi).join(', ') + ' talebinin gönderilmesi. (' + takipler + ')';
                    break;
                case TaskTuru.mts_tebligat_gonder:
                    aciklama = 'MTS tebligatının gönderilmesi. (' + takipler + ')'
                    break;
                case TaskTuru.makbuz_indir:
                    aciklama = 'Makbuzların indirilmesi. (' + takipler + ')'
                    break;
                case TaskTuru.takip_hazirla:
                    aciklama = payload.anaTask.data.xmlName + " XML'indeki takiplerin tevzi alınması.";
                    break;
                case TaskTuru.hizli_takip_ac:
                    aciklama = payload.anaTask.data.xmlName + " XML'indeki takiplerin tevzi alınması ve açılması.";
                    break;
                case TaskTuru.mts_takip_hazirla:
                    aciklama = payload.anaTask.data.xmlName + " XML'indeki MTS takiplerinin tevzi alınması.";
                    break;
                case TaskTuru.takip_ac:
                    aciklama = takiplerEsasNoYok + ' takiplerinin açılması.'
                    break;
                case TaskTuru.mts_takip_ac:
                    aciklama = takiplerEsasNoYok + ' MTS takiplerinin açılması.'
                    break;
                case TaskTuru.mts_hizli_takip_ac:
                    aciklama = payload.anaTask.data.xmlName + " XML'indeki MTS takiplerin tevzi alınması ve açılması.";
                    break;
                default:
                    break;
            }
            resolve(aciklama);
        })
    }
}

export default {
    state,
    getters,
    mutations,
    actions
}
